import {
  faCalendarClock,
  faCube,
  faRefresh,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { startOfToday, endOfToday, subDays } from "date-fns";
import { isEmpty } from "lodash";
import { useState, useMemo, useEffect, useCallback } from "react";
import { DateRange } from "react-day-picker";

import { DateRangePicker } from "src/base-components/DateRangePicker";
import { Divider } from "src/base-components/Divider";
import { Icon } from "src/base-components/Icon";
import {
  Cell,
  ColumnVisibilityDropdown,
  TableComp,
} from "src/base-components/Table";
import { Tabs, TabData } from "src/base-components/Tabs";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import { PinButton } from "src/cases/common/PinButton";
import { RelatedBlockTabLabel } from "src/cases/common/RelatedBlockTabLabel";
import { useUpdateBlockConfig } from "src/cases/queries/cases";
import {
  CaseEnvironment,
  PinnedEvent,
  RelatedEventsCaseBlockConfigAttributes,
} from "src/clients/case-management";
import { EventConfigEnriched } from "src/clients/features-control";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";
import { useEntitySchemas } from "src/entities/queries";
import { getTableStoreKey } from "src/eventsCatalogue/EventsCatalogue";
import {
  useEvents,
  useEventSchemas,
  eventSchemaKeys,
  eventsKeys,
  DeserializedEvent,
} from "src/eventsCatalogue/queries";
import { getColumns } from "src/eventsCatalogue/tableConfig";
import { Environment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { CasePageParams } from "src/router/urls";
import { useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

const DEFAULT_TIME_WINDOW_DAYS = 7;

const helper = createColumnHelper<DeserializedEvent>();

type RelatedEventsBlockProps = Omit<CaseBlockProps, "children"> & {
  data: RelatedEventsCaseBlockConfigAttributes;
  caseEnv?: CaseEnvironment;
};

type EventTabContentProps = {
  eventType: string;
  entityId: string | null;
  environment: "live" | "sandbox";
  entitiesSchemasData: ReturnType<typeof useEntitySchemas>["data"];
  dateRangePickerValue: DateRange | undefined;
  schema: EventConfigEnriched | undefined;
  pinnedEvents: PinnedEvent[];
  onEventPinChange: (eventId: string, pinned: boolean) => void;
};

const buildEventColumnsWithPinAction = (
  schema: EventConfigEnriched,
  entitiesSchemasData: ReturnType<typeof useEntitySchemas>["data"],
  filters: Record<string, string | null>,
  onFilterChange: (filters: Record<string, string | null>) => void,
  pinnedEvents: PinnedEvent[],
  onEventPinChange: (eventId: string, pinned: boolean) => void,
  environment: Environment,
) => {
  const baseColumns = getColumns(schema, environment, entitiesSchemasData, {
    filters,
    onFilterChange,
  });
  const columnsWithoutActions = baseColumns.filter(
    (col) => col.id !== "actions",
  );

  const pinColumn = helper.display({
    id: "pin",
    header: (info) => (
      <div className="flex h-8 items-center justify-center bg-white">
        <ColumnVisibilityDropdown min={3} table={info.table} />
      </div>
    ),
    size: 54,
    minSize: 54,
    maxSize: 54,
    meta: {
      sticky: "right",
    },
    cell: (info) => {
      const eventId = info.row.original.payload.id;
      const isPinned = pinnedEvents.some((e) => e.id === eventId);

      return (
        <Cell
          className="items-center justify-center group-hover:bg-gray-50"
          info={info}
        >
          <PinButton
            dataLoc={`event-${eventId}`}
            pinned={isPinned}
            onChange={(pinned) => onEventPinChange(eventId, pinned)}
          />
        </Cell>
      );
    },
  });

  return [...columnsWithoutActions, pinColumn];
};

const EventTabContent: React.FC<EventTabContentProps> = ({
  eventType,
  entityId,
  environment,
  entitiesSchemasData,
  dateRangePickerValue,
  schema,
  pinnedEvents,
  onEventPinChange,
}) => {
  const [filters, setFilters] = useState<Record<string, string | null>>({});

  const timeWindow = useMemo(() => {
    if (!dateRangePickerValue?.from || !dateRangePickerValue?.to) {
      return {
        from: subDays(startOfToday(), DEFAULT_TIME_WINDOW_DAYS).toISOString(),
        to: endOfToday().toISOString(),
      };
    }
    return {
      from: dateRangePickerValue.from.toISOString(),
      to: dateRangePickerValue.to.toISOString(),
    };
  }, [dateRangePickerValue]);

  const {
    data: events,
    isLoading: isEventsLoading,
    isRefetching: isRefetchingEvents,
  } = useEvents(
    {
      eventType,
      timeWindow,
      entityId,
      environment,
      filters,
    },
    { enabled: !!entityId },
  );

  const memoizedColumns = useMemo(() => {
    if (!schema) return [];

    return buildEventColumnsWithPinAction(
      schema,
      entitiesSchemasData,
      filters,
      setFilters,
      pinnedEvents,
      onEventPinChange,
      environment,
    );
  }, [
    schema,
    entitiesSchemasData,
    filters,
    pinnedEvents,
    onEventPinChange,
    environment,
  ]);

  const isLoading = isEventsLoading || isRefetchingEvents || !schema;
  const hasNoEvents = !isLoading && events?.length === 0;
  const hasFilters =
    !isEmpty(filters) && Object.values(filters).some((value) => value);

  if (hasNoEvents && !hasFilters) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <EmptyState
          dataLoc={`events-list-${eventType}-empty-state`}
          description="No events found for the selected time period"
          headline="No events"
          icon={faSearch}
          size="sm"
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-4">
      <div className="min-h-0 flex-1">
        <TableComp
          columns={memoizedColumns}
          data={events || []}
          dataLoc={`events-list-${eventType}-table`}
          frameClassName="h-full"
          isLoading={isLoading}
          rowClassName="group hover:bg-gray-50"
          uiStoreKey={getTableStoreKey(`events-list-${eventType}`)}
          variant="compact"
        />
      </div>
      {hasNoEvents && hasFilters && (
        <EmptyState
          dataLoc={`events-list-${eventType}-empty-state-filtered`}
          description="Try adjusting the filter to find what you are looking for"
          headline="No events found"
          icon={faSearch}
          size="sm"
        />
      )}
    </div>
  );
};

export const RelatedEventsBlock: React.FC<RelatedEventsBlockProps> = ({
  data,
  caseEnv,
  ...props
}) => {
  const { workspace } = useWorkspaceContext();
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const queryClient = useQueryClient();
  const env = caseEnv === CaseEnvironment.LIVE ? "live" : "sandbox";
  const updateBlockConfigMutation = useUpdateBlockConfig();

  const {
    data: eventTypes,
    isLoading: isLoadingEventTypes,
    isFetching: isRefreshing,
  } = useEventSchemas();
  const [selectedTab, setSelectedTab] = useState<string>("");

  const { dateRangePickerValue, onDateRangePickerChange } = useTimeWindow(
    {
      from: subDays(startOfToday(), DEFAULT_TIME_WINDOW_DAYS),
      to: endOfToday(),
    },
    `events-list-block-time-window`,
  );

  const { data: entitiesSchemasData } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });

  useEffect(() => {
    if (eventTypes && eventTypes.length > 0 && !selectedTab) {
      setSelectedTab(eventTypes[0].event_type);
    }
  }, [eventTypes, selectedTab]);

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from && range?.to) {
      onDateRangePickerChange(range);
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: eventSchemaKeys.workspaceList(workspace.id),
    });
    if (selectedTab) {
      queryClient.invalidateQueries({
        queryKey: eventsKeys.schemaEvents(
          workspace.id,
          selectedTab,
          data.entity.id,
        ),
      });
    }
  };

  const handleEventPinChange = useCallback(
    (eventId: string, pinned: boolean) => {
      if (!selectedTab) return;

      const currentPinnedEntities = data.pinned_events || [];
      const isAlreadyPinned = currentPinnedEntities.some(
        (e) => e.id === eventId,
      );

      let updatedEvents: PinnedEvent[];
      if (pinned) {
        updatedEvents = isAlreadyPinned
          ? currentPinnedEntities
          : [
              ...currentPinnedEntities,
              { id: eventId, event_type: selectedTab },
            ];
      } else {
        updatedEvents = currentPinnedEntities.filter((e) => e.id !== eventId);
      }

      updateBlockConfigMutation.mutate({
        caseId,
        blockKey: props.blockKey,
        attributes: {
          pinned_events: updatedEvents,
        },
      });
    },
    [
      data.pinned_events,
      selectedTab,
      caseId,
      props.blockKey,
      updateBlockConfigMutation,
    ],
  );

  const customButton = (
    <div className="ml-auto flex h-11 items-center">
      <Divider orientation="vertical" />
      <div className="p-1.5">
        <DateRangePicker
          value={dateRangePickerValue}
          borderless
          suffixIcon
          onChange={handleDateRangeChange}
        />
      </div>
      <Divider orientation="vertical" />
      <Tooltip delay={350} placement="bottom" title="Refresh" asChild>
        <div className="p-3.5">
          <Icon
            color="text-gray-500"
            data-loc="events-refresh"
            icon={faRefresh}
            size="sm"
            spin={isRefreshing}
            onClick={handleRefresh}
          />
        </div>
      </Tooltip>
    </div>
  );

  const tabs: TabData[] = useMemo(() => {
    if (!eventTypes) return [];

    return eventTypes.map((eventType) => {
      const filteredPinnedEvents = (data.pinned_events || []).filter(
        (e) => e.event_type === eventType.event_type,
      );

      return {
        key: eventType.event_type,
        label: (
          <RelatedBlockTabLabel pinnedCount={filteredPinnedEvents.length}>
            {eventType.display_name_plural || eventType.event_type}
          </RelatedBlockTabLabel>
        ),
        content: (
          <EventTabContent
            dateRangePickerValue={dateRangePickerValue}
            entitiesSchemasData={entitiesSchemasData}
            entityId={data.entity.id}
            environment={env}
            eventType={eventType.event_type}
            pinnedEvents={filteredPinnedEvents}
            schema={eventType}
            onEventPinChange={handleEventPinChange}
          />
        ),
      };
    });
  }, [
    eventTypes,
    data.entity.id,
    data.pinned_events,
    entitiesSchemasData,
    env,
    dateRangePickerValue,
    handleEventPinChange,
  ]);

  if (!eventTypes?.length && !isLoadingEventTypes) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="events-list-empty-state"
          description="There are no events found related to this entity yet."
          headline="No events found"
          icon={faCalendarClock}
          size="sm"
        />
      </CaseBlock>
    );
  }

  if (!data.entity.id) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          dataLoc="events-list-empty-state"
          description="This block currently has no linked entity."
          headline="No entity provided"
          icon={faCube}
          size="sm"
        />
      </CaseBlock>
    );
  }

  return (
    <CaseBlock
      {...props}
      isLoading={isLoadingEventTypes}
      noPaddings={true}
      noScroll={true}
    >
      <Tabs
        containerClassName="h-full flex flex-col"
        customButton={customButton}
        panelClassName="flex-1 min-h-0 overflow-hidden"
        panelsClassName="h-full flex flex-col"
        selectedKey={selectedTab}
        tabClassName="px-2 h-11 flex items-center"
        tabListClassName="flex-shrink-0 flex items-center h-11 border-b border-gray-200 bg-white pl-2.5"
        tabs={tabs}
        unmountPanels={false}
        onChange={setSelectedTab}
      />
    </CaseBlock>
  );
};
